import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Typography, Table, Space, Modal, Form, Button } from 'antd'
import { DeleteOutlined, PlusOutlined, UploadOutlined, ExclamationCircleOutlined } from '@ant-design/icons'
import { getBanners, createBanner, deleteBanner } from '../actions/bannerAction'
import { CustomButton } from '../components/ui/CustomButton'
import { CustomUpload } from '../components/ui/CustomUpload'
import Colors from '../utils/colors'
import Constants from '../utils/constants'
import { showToast } from '../utils/toast'

const { Title } = Typography

const Banners = () => {
  const dispatch = useDispatch()
  const { loading: createLoading, error: createError, success: createSuccess } = useSelector((state) => state.createBanner)
  const { data: banners, totalBanners, loading } = useSelector((state) => state.bannerList)
  const { success: isDeleted, loading: deleteLoading, error: deleteError } = useSelector((state) => state.deleteBanner)

  const [currentPage, setCurrentPage] = useState(1)
  const [limit] = useState(Constants.ITEMS_PER_PAGE)
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [selectedBannerId, setSelectedBannerId] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)
  const [form] = Form.useForm()

  useEffect(() => {
    dispatch(getBanners({ page: currentPage, perPage: limit }))
  }, [dispatch])

  useEffect(() => {
    if (createSuccess) {
      showToast('Banner created successfully', 'success')
      setCreateModalVisible(false)
      setSelectedFile(null)
      setPreviewUrl(null)
      form.resetFields()
      dispatch(getBanners({ page: 1, perPage: limit }))
    }

    if (createError) {
      showToast(createError, 'error')
    }
  }, [createSuccess, createError])

  useEffect(() => {
    if (isDeleted) {
      showToast('Banner deleted successfully', 'success')
      setDeleteModalVisible(false)
      setSelectedBannerId(null)
      dispatch(getBanners({ page: currentPage, perPage: limit }))
    }
    if (deleteError) {
      showToast(deleteError, 'error')
    }
  }, [isDeleted, deleteError])

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => (currentPage - 1) * limit + index + 1,
      width: 50
    },
    {
      title: 'Banner',
      dataIndex: 'url',
      key: 'url',
      render: (url) => (
        <img 
          src={url} 
          alt="Banner" 
          style={{ 
            width: '100px', 
            height: '60px', 
            objectFit: 'cover', 
            borderRadius: '4px' 
          }} 
        />
      ),
      width: 120
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <CustomButton 
            size="small"
            onClick={() => handleDelete(record._id)}
            className="delete-btn"
            style={{ 
              height: '28px',
              fontSize: '10px',
              width: 'auto'
            }}
          >
            <DeleteOutlined /> Delete
          </CustomButton>
        </Space>
      ),
      width: 100
    }
  ]

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current)
    dispatch(getBanners({ page: pagination.current, perPage: limit }))
  }

  const handleCreateSubmit = () => {
    if (!selectedFile) {
      showToast('Please select a banner image', 'error')
      return
    }
    
    const formData = new FormData()
    formData.append('banner', selectedFile)
    dispatch(createBanner(formData))
  }

  const handleFileChange = (info) => {
    if (info.file) {
      setSelectedFile(info.file)
      const url = URL.createObjectURL(info.file)
      setPreviewUrl(url)
    }
  }

  const handleCreateCancel = () => {
    setCreateModalVisible(false)
    setSelectedFile(null)
    setPreviewUrl(null)
    form.resetFields()
  }

  const handleDelete = (bannerId) => {
    setSelectedBannerId(bannerId)
    setDeleteModalVisible(true)
  }

  const confirmDelete = () => {
    if (selectedBannerId) {
      dispatch(deleteBanner(selectedBannerId))
    }
  }

  const cancelDelete = () => {
    setDeleteModalVisible(false)
    setSelectedBannerId(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <Title level={4} style={{ color: Colors.primary, margin: 0 }}>
          All Banners
        </Title>
        <CustomButton
          type="primary"
          onClick={() => setCreateModalVisible(true)}
          className='create-btn'
          style={{ 
            height: '28px',
            fontSize: '10px',
            width: 'auto'
          }}
        >
          <PlusOutlined /> Add Banner
        </CustomButton>
      </div>
      
      <div style={{ width: '100%', overflowX: 'auto', border: `1px solid ${Colors.darkerGray}`, borderRadius: 10 }}>
        <Table
          columns={columns}
          dataSource={banners}
          loading={loading}
          rowKey={(record) => record._id}
          pagination={{
            current: currentPage,
            total: totalBanners,
            pageSize: limit,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} banners`,
            itemRender: (current, type, originalElement) => {
              if (type === 'page') {
                return (
                  <span style={{
                    backgroundColor: current === currentPage ? Colors.primary : 'transparent',
                    color: current === currentPage ? Colors.white : Colors.primary,
                    border: `1px solid ${Colors.primary}`,
                    padding: '4px 8px',
                    borderRadius: 4,
                    cursor: 'pointer'
                  }}>
                    {current}
                  </span>
                )
              }
              return originalElement
            }
          }}
          onChange={handleTableChange}
          scroll={{ x: 800 }}
          size="middle"
        />
      </div>

      <Modal
        title="Add New Banner"
        open={createModalVisible}
        onOk={handleCreateSubmit}
        onCancel={handleCreateCancel}
        okText="Create Banner"
        cancelText="Cancel"
        confirmLoading={createLoading}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="banner"
            label="Banner Image"
            rules={[{ required: true, message: 'Please upload a banner image' }]}
          >
            <CustomUpload
              beforeUpload={() => false}
              onChange={handleFileChange}
              maxCount={1}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Select Banner Image</Button>
            </CustomUpload>
            {previewUrl && (
              <div style={{ marginTop: '16px', textAlign: 'center' }}>
                <img 
                  src={previewUrl} 
                  alt="Preview" 
                  style={{ 
                    maxWidth: '100%', 
                    maxHeight: '200px', 
                    borderRadius: '4px',
                    border: `1px solid ${Colors.lightGray}`
                  }} 
                />
              </div>
            )}
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Delete Banner"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={cancelDelete}
        okText="Yes, Delete"
        cancelText="Cancel"
        okType="danger"
        centered
        confirmLoading={deleteLoading}
      >
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <ExclamationCircleOutlined style={{ color: Colors.warning, fontSize: '22px', marginRight: 12 }} />
          <span>Are you sure you want to delete this banner?</span>
        </div>
      </Modal>
    </div>
  )
}

export default Banners